import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DroppableProvided, DroppableStateSnapshot } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { COMMON_STYLE_EXCLUDE_COMP } from '../../constants';
import { RootState } from '../../modules';
import { copyContent, loadSavedBoxesAsync, moveContent, removeContent, selectContent, setDefaultShop, setShops, switchContent } from '../../modules/editor';
import { loadSavedBoxes, saveSavedBox } from '../../modules/editor/service';

import useAlert from '../../hooks/useAlert';
import useDebounce from '../../hooks/useDebounce';

import { faBookmark } from '@fortawesome/pro-regular-svg-icons/faBookmark';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { faCopy } from '@fortawesome/pro-regular-svg-icons/faCopy';
import { faGripDotsVertical } from '@fortawesome/pro-regular-svg-icons/faGripDotsVertical';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons/faTrashCan';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';


import SaveModal from '../../components/InputModal';
import { Container, Contents } from '../../modules/editor/types';
import Content from './Content';
import ContentsListDropzone from './ContentsListDropzone';

import icon_switch from '../../assets/images/icon/switch.png';
import { TEXT_TEMPLATE } from '../../constants';
import { requestShops } from '../../modules/editor/service';

type Props = {
	list: Contents;
	container: Container;
	onDrop: (candrop: boolean) => void;
};

function ContentsList({ list, container, onDrop }: Props) {
	const dispatch = useDispatch();
	const Alert = useAlert();
	const service = useSelector((state: RootState) => state.auth.service);

	const [items, setItems] = useState([] as any);
	const [dbCanDrop, canDrop, setCanDrop] = useDebounce<boolean>(
		false,
		10
	);
	const [dbCanDropItem, canDropItem, setCanDropItem] = useDebounce<number>(
		-1,
		10
	);
	const [placeholderProps, setPlaceholderProps] = useState({} as any);

	const listRef = useRef<HTMLDivElement>(null);

	const [listHeight, setListHeight] = useState(0);

	// 저장 모달 관련 state
	const [showSaveModal, setShowSaveModal] = useState(false);
	const [currentSaveContentId, setCurrentSaveContentId] = useState<number>(-1);

	const selectedContentId = useSelector((state: RootState) => state.editor.selectedContentId);
	const isContentsLoading = useSelector((state: RootState) => state.editor.ui.isContentsLoading);
	const contentPreset = useSelector((state: RootState) => state.auth.account.options.contentPreset);
	const { templateId, listId, isListDeleted } = useSelector((state: RootState) => state.editor.email);
	const hasNoList = listId === -1 || listId === undefined || isListDeleted;
	const savedBoxesMeta = useSelector((state: RootState) => state.editor.savedBoxesMeta);
	const savedBoxesLoading = useSelector((state: RootState) => state.editor.savedBoxesLoading);
	const savedBoxes = useSelector((state: RootState) => state.editor.savedBoxes);
	const commonStyle = useSelector((state: RootState) => state.editor.container.commonStyle);

	const contentListStyle = useMemo(() => {
		const style: Omit<Container, 'pallette' | 'commonStyle'> & { pallette?: Container['pallette'], commonStyle?: Container['commonStyle'] } = {
			...container,
			borderStyle: container.borderStyle.length === 0 ? 'solid' : container.borderStyle,
		};
		delete style.pallette;
		delete style.commonStyle;
		return style
	}, [container]);

	useEffect(() => {
		if (listRef?.current) {
			setListHeight(listRef?.current?.scrollHeight + 270);
		}
	}, [dbCanDrop])

	useEffect(() => {
		setItems(list);
	}, [list])

	useEffect(() => {
		onDrop(dbCanDrop);
	}, [dbCanDrop])

	const reOrder = (list:any, startIndex:any, endIndex:any) => {
		const result = Array.from(list);
		const [removed] = result.splice(startIndex, 1);
		result.splice(endIndex, 0, removed);

		setItems(result);
		dispatch(moveContent(result));
	};

	const getItemStyle = (draggableStyle:any, isDragging:any) => ({
		...draggableStyle,
		userSelect: 'none',
	});

	const onDragEnd = (result:any) => {
		// dropped outside the list(리스트 밖으로 드랍한 경우)
		if (!result.destination) {
			setCanDrop(false);
			return;
		}

		reOrder(
			items,
			result.source.index,
			result.destination.index
		);

		setCanDrop(false);
	}

	const onMouseOver = (e: any) => {
		if (canDrop) {
			return;
		}
		const { currentTarget } = e;
		currentTarget.classList.add('hover');
	}

	const onMouseOut = (e: any) => {
		const { currentTarget } = e;
		currentTarget.classList.remove('hover');
	}

	const selectItem = (e:any, id:number) => {
		e.stopPropagation();
		if (selectedContentId === id) {
			return;
		}
		dispatch(selectContent(id));
	}

	const movePrev = (e: any, index:number) => {
		e.stopPropagation();
		if (index === 0) {
			return;
		}

		reOrder(
			items,
			index,
			index-1
		);
	}

	const moveNext = (e: any, index:number) => {
		e.stopPropagation();
		if (index + 1 >= items.length) {
			return;
		}

		reOrder(
			items,
			index,
			index + 1
		);
	}

	const remove = (id:number) => {
		Alert.confirm(Alert?.msg?.BOX_REMOVE, () => {
			dispatch(removeContent(id));
		});
	}

	const copy = (index:number) => {
		dispatch(copyContent({ seq: index + 1, originalIndex: index }));
	}

	const save = (id:number) => {
		if (service.isStarter) {
			window.parent.postMessage({ msg: 'openUpgradeGuideModal', guideType: 'saveBox' }, '*');
			return;
		}

		// savedBoxes가 로드되어 있지 않으면 먼저 로드
		if (!savedBoxesMeta && !savedBoxesLoading) {
			dispatch(loadSavedBoxesAsync.request({ offset: 0, limit: 33, isLoadMore: false }));
		}

		// 개수 제한이 있는 경우에만 체크
		if (contentPreset?.maxCount) {
			const currentCount = savedBoxesMeta?.total || 0;

			if (currentCount >= contentPreset.maxCount) {
				Alert.error(`상자는 ${contentPreset.maxCount}개까지 저장할 수 있습니다.`);
				return;
			}
		}

		setCurrentSaveContentId(id);
		setShowSaveModal(true);
		window.parent.postMessage({ msg: 'dialogShow' }, '*');
	}

	const handleSaveModalClose = () => {
		setShowSaveModal(false);
		setCurrentSaveContentId(-1);
		window.parent.postMessage({ msg: 'dialogHide' }, '*');
	}

	// 중복 상자 이름 존재 여부 확인
	const isDuplicateName = (name: string) => {
		return savedBoxes.some((box: any) => box.name === name.trim());
	};

	const handleSave = async (name: string): Promise<void> => {
		if (currentSaveContentId === -1) {
			return;
		}

		const content = list?.find((item: any) => item.id === currentSaveContentId);

		if (!content) {
			Alert.error('저장할 컨텐츠를 찾을 수 없습니다.');
			return;
		}

		const markedContent = markContentAsEdited(content.content);

		const boxData = {
			name,
			preset: markedContent
		};

		await saveSavedBox(boxData);
		Alert.log('상자를 저장했습니다.');
		// 현재 로드된 상자가 전체 상자 개수와 같다면 +1 (새로 추가된 상자 포함)
		const isAllLoaded = savedBoxesMeta && savedBoxes.length >= savedBoxesMeta.total;
		const currentLimit = Math.max(savedBoxes.length + (isAllLoaded ? 1 : 0), 33);
		dispatch(loadSavedBoxesAsync.request({ offset: 0, limit: currentLimit, isLoadMore: false }));
	}

	// 상자를 "수정된 상자"로 마킹하여 다른 이메일에서 전체 스타일의 영향을 받지 않도록 처리
	const markContentAsEdited = (contentData: any) => {
		// 원본 콘텐츠를 깊은 복사하여 저장용 콘텐츠 생성 (원본 상자는 화면에서 그대로 유지)
		const markedContent = JSON.parse(JSON.stringify(contentData));

		// 저장되는 상자가 다른 이메일에서 불러와질 때 전체 스타일의 영향을 받지 않도록 edited 마킹
		if (!COMMON_STYLE_EXCLUDE_COMP.includes(markedContent.category)) {
			const edited = markedContent.edited || [];

			// 현재 상자의 모든 스타일을 "사용자가 수정한 것"으로 마킹하여 전체 스타일 적용 방지
			['text', 'box', 'partition', 'button0', 'button1'].forEach(styleType => {
				if (!edited.includes(styleType)) {
					edited.push(styleType);
				}
			});
			markedContent.edited = edited;
		}

		return markedContent;
	};

	const onDragOver = (e:any) => {
		e.preventDefault();
		if (e.dataTransfer.types.includes('text/plain')) {
			setCanDrop(true);
		}
		// dropEffect를 move로 설정.
		e.dataTransfer.dropEffect = "move";
	}

	const onDragLeave = (e:any) => {
		setCanDrop(false);
	}

	const onDragOverItem = (index:number, e:any) => {
		if (e.dataTransfer.types.includes('text/plain') && index !== canDropItem) {
			setCanDropItem(index);
		}
	}

	const onDragLeaveItem = () => {
		setCanDropItem(-1);
	}

	const getListClass = (isDragging:boolean) => {
		return isDragging ? 'content-list-dropzone dragging' : 'content-list-dropzone'
	}

	const onContainerDragStart = (e:any) => {
		setCanDrop(true);
	}

	const onContainerDragUpdate = (e:any) => {
		if (!e.destination) {
      return;
    }

    const draggedDOM:any = getDraggedDom(e.draggableId);

    if (!draggedDOM) {
      return;
    }

		const { clientHeight, clientWidth } = draggedDOM;
    const destinationIndex = e.destination.index;
    const sourceIndex = e.source.index;

		const updatedArray:any = Array.from(document.querySelectorAll('.content-list-dropzone > div'));
		if (sourceIndex < destinationIndex) {
      updatedArray.splice(sourceIndex, 1);
    }

    var clientY =
      updatedArray.slice(0, destinationIndex).reduce((total:any, curr:any) => {
				if (curr !== undefined) {
					const style = curr?.currentStyle || window?.getComputedStyle(curr);
					const marginBottom = parseFloat(style?.marginBottom);
					return total + curr?.clientHeight + marginBottom;
				} else {
					return 0;
				}
			}, 0);

		setPlaceholderProps({
			clientHeight,
			clientWidth,
			clientY,
			clientX: parseFloat(
				window.getComputedStyle(draggedDOM.parentNode).paddingLeft
			)
		});
	}

	const getDraggedDom = (draggableId: number) => {
		const domQuery = `[data-rbd-draggable-id='${draggableId}']`;
		const draggedDOM = document.querySelector(domQuery);

		return draggedDOM;
	};

	const switchCol = (id: number) => {
		dispatch(switchContent(id));
	}

	const prepareShopProductItem = async () => {
    const shops = await requestShops();
    dispatch(setShops(shops));
    dispatch(setDefaultShop(shops[0]));
	}

	return (
		<>
			<div
				className="content-list"
				style={items?.length === 0 ? {} : contentListStyle}
			>
			{isContentsLoading ? (<div className="content-list-loading"></div>) : (
				<div
					onDragOver={onDragOver}
					onDragLeave={onDragLeave}
					onDragEnd={onDragLeave}
					onDrop={onDragLeave}
					ref={listRef}
				>
					{
						items?.length === 0 ? (
							<div className="content-list-empty">
								<ContentsListDropzone
									canDrop={dbCanDrop}
									seq={0}
								/>
								{
									!dbCanDrop && (
										<p>
											오른쪽 <strong>상자</strong> 메뉴에서 상자를 끌어다 놓으세요. 👉
										</p>
									)
								}
							</div>
						) : (
							<DragDropContext
								onDragEnd={onDragEnd}
								onDragStart={onContainerDragStart}
								onDragUpdate={onContainerDragUpdate}
							>
								<Droppable droppableId="contentList">
									{(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
										<div
											{...provided.droppableProps}
											ref={provided.innerRef}
											className={getListClass(snapshot.isDraggingOver)}
										>
											{items?.map((item: any, index: number) => (
												<div key={item.id} style={{ position: 'relative', padding: dbCanDropItem === index ? index === 0 ? '54px 0 54px 0' : '0 0 54px 0' : '0 0' }} >
													<div
														onDragOver={(e) => onDragOverItem(index, e)}
														onDragLeave={onDragLeaveItem}
														onDragEnd={onDragLeaveItem}
														onDrop={onDragLeaveItem}
													>
														<ContentsListDropzone
															canDrop={dbCanDropItem === index}
															seq={index}
														/>
														<Draggable
															index={index}
															key={item.id}
															draggableId={`${item.id}`}
														>
															{
																(provided, snapshot) => (
																	<div>
																		{templateId === TEXT_TEMPLATE ? (
																			<Content
																				contentItem={item}
																				key={index}
																			/>
																		) : (
																			<div
																				className={selectedContentId === item.id ? 'content-wrapper selected' : snapshot.isDragging ? 'content-wrapper dragging' : 'content-wrapper'}
																				onMouseOver={onMouseOver}
																				onMouseOut={onMouseOut}
																				onClick={async (e) => {
																					if (item.content.category === 'shop' && hasNoList) {
																						prepareShopProductItem();
																					};
																					selectItem(e, item.id);
																				}}

																				ref={provided.innerRef}
																				{...provided.draggableProps}
																				style={getItemStyle(
																					provided.draggableProps.style,
																					snapshot.isDragging
																				)}
																			>
																				<Content
																					contentItem={item}
																					key={index}
																				/>
																				<div className="content-handler">
																					<div
																						{...provided.dragHandleProps}
																						className="action move"
																					>
																						<FontAwesomeIcon icon={faGripDotsVertical} size="lg" />
																					</div>
																					<button onClick={(e) => { movePrev(e, index) }} className="action prev">
																						<FontAwesomeIcon icon={faChevronUp} />
																					</button>
																					<button onClick={(e) => { moveNext(e, index) }} className="action next">
																						<FontAwesomeIcon icon={faChevronDown} />
																					</button>
																					<button onClick={(e: any) => { copy(index); e.stopPropagation() }} className="action copy">
																						<FontAwesomeIcon icon={faCopy} />
																					</button>
																					<button onClick={(e: any) => { remove(item.id); e.stopPropagation() }} className="action remove">
																						<FontAwesomeIcon icon={faTrashCan} />
																					</button>
																					<button onClick={(e: any) => { save(item.id); e.stopPropagation() }} className='action save'>
																						<FontAwesomeIcon icon={faBookmark} />
																					</button>
																				</div>
																				{
																					selectedContentId === item.id && item?.content?.column?.length === 2 && (
																						<div className="content-switch">
																							<button onClick={() => { switchCol(item.id) }}>
																								<img src={icon_switch} alt="switch" width="34" />
																							</button>
																						</div>
																					)
																				}
																			</div>
																		)}
																	</div>
																)
															}
														</Draggable>
													</div>
												</div>
											))}

											{provided.placeholder}
											{snapshot.isDraggingOver && (
												<div
													className="placeholder"
													style={{
														top: placeholderProps.clientY,
														left: placeholderProps.clientX,
														height: placeholderProps.clientHeight,
														width: placeholderProps.clientWidth
													}}
												>
													<p>
														여기로 상자를 이동합니다.
													</p>
												</div>
											)}
										</div>
									)}
								</Droppable>
							</DragDropContext>
						)
					}
				</div>
			)}
			</div>

					{/* 저장 모달 */}
		<SaveModal
			title='상자를 저장합니다'
			placeholder=''
			show={showSaveModal}
			onClose={handleSaveModalClose}
			onSave={handleSave}
			checkDuplicate={isDuplicateName}
		/>
		</>
	);
}

export default ContentsList;